/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HealthMonitorModule
 */

import {
    Component,
    Inject,
    Input,
    OnInit,
    SimpleChanges,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import {
    HealthMonitor,
    HealthMonitorFtp,
    HealthMonitorSSLAttributes,
} from 'object-types';

import {
    CertificateCollection,
} from 'ajs/modules/security/factories/certificates/certificate.collection.factory';

import {
    PKIProfileCollection,
} from 'ajs/modules/security/factories/pki-profile/pki-profile.collection.factory';

import {
    SSLProfileCollection,
} from 'ajs/modules/security/factories/ssl-profile/ssl-profile.collection.factory';

import type {
    HealthMonitorItem,
} from 'ajs/modules/healthmonitor/factories/health-monitor-item.factory';

import type {
    HealthMonitorFtpConfigItem,
} from 'ajs/modules/healthmonitor/factories/health-monitor-ftp.config-item.factory';

import * as globalL10n from 'global-l10n';
import * as l10n from './ftp-health-monitor-config.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * CertificateCollection Type.
 */
type TCertificateCollection = typeof CertificateCollection;

/**
 * PKIProfileCollection Type.
 */
type TPkiProfileCollection = typeof PKIProfileCollection;

/**
 * SSLProfileCollection Type.
 */
type TSslProfileCollection = typeof SSLProfileCollection;

/**
 * @description Component to display the FTP/FTPS section in the Health Monitor modal.
 * @author Sarthak Kapoor
 */
@Component({
    selector: 'ftp-health-monitor-config',
    templateUrl: './ftp-health-monitor-config.component.html',
})
export class FtpHealthMonitorConfigComponent implements OnInit {
    @Input()
    public editable: HealthMonitorItem;

    /**
     * Passed as a param to ssl, pki and certificate profile collections.
     */
    @Input()
    public isFederated = false;

    /**
     * Health Monitor config type. This can be either smtp_monitor or smtps_monitor.
     */
    public ftpConfigItem: HealthMonitorFtpConfigItem;

    /**
     * Flag to show fields applicable only for SMTPS type.
     */
    public isFtpsType = false;

    public readonly certificateCollection: CertificateCollection;

    public readonly pkiProfileCollection: PKIProfileCollection;

    public readonly sslProfileCollection: SSLProfileCollection;

    public readonly objectTypes = {
        HealthMonitor,
        HealthMonitorFtp,
        HealthMonitorSSLAttributes,
    };

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    constructor(
        l10nService: L10nService,
        @Inject(CertificateCollection)
        CertificateCollection: TCertificateCollection,
        @Inject(PKIProfileCollection)
        PKIProfileCollection: TPkiProfileCollection,
        @Inject(SSLProfileCollection)
        SSLProfileCollection: TSslProfileCollection,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.certificateCollection = new CertificateCollection();
        this.pkiProfileCollection = new PKIProfileCollection();
        this.sslProfileCollection = new SSLProfileCollection();
    }

    /** @override */
    public ngOnInit(): void {
        if (this.editable.isFtpsType()) {
            this.ftpConfigItem = this.editable.config.ftps_monitor;
            this.isFtpsType = true;
        } else {
            this.ftpConfigItem = this.editable.config.ftp_monitor;
        }
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { isFederated } = changes;
        const params = {
            is_federated: isFederated?.currentValue,
        };

        this.sslProfileCollection.setParams(params);
        this.pkiProfileCollection.setParams(params);
        this.certificateCollection.setParams(params);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.sslProfileCollection.destroy();
        this.pkiProfileCollection.destroy();
        this.certificateCollection.destroy();
    }
}
